import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Spinner } from '@/components/atoms/Spinner';
import { ModalComponent } from '@/components/molecules/ModalComponent';
import Tabs from '@/components/molecules/Tabs';
import SomethingHappened from '@/components/SomethingHappened';
import {
    type FishGroupDetail,
    useGetFishGroupDetail,
    useGetFishGroupLineage,
} from '@/domain/fishGroups';
import { formatDateTimeWorded } from '@/utils/date';

import FishGroupActionsBar from '../actions/FishGroupActionsBar';
import FishGroupStatusBadge from '../FishGroupStatusBadge';

import EventsTab from './tabs/EventsTab';
import LineageTab from './tabs/LineageTab';
import LocationsTab from './tabs/LocationsTab';
import MetadataTab from './tabs/MetadataTab';

import styles from './FishGroupDetailModal.module.scss';

type TabKey = 'metadata' | 'locations' | 'events' | 'lineage';

interface Props {
    clientId: number;
    fishGroupId: number | null;
    isOpen: boolean;
    onClose: () => void;
}

export default function FishGroupDetailModal(props: Props) {
    const { clientId, fishGroupId, isOpen, onClose } = props;
    const { t } = useTranslation('fishGroups');
    const [activeFishGroupId, setActiveFishGroupId] = useState<number | null>(fishGroupId);
    const [activeTab, setActiveTab] = useState<TabKey>('metadata');

    // Sync internal state when the parent opens the modal for a different FG.
    useEffect(() => {
        if (fishGroupId !== null) {
            setActiveFishGroupId(fishGroupId);
            setActiveTab('metadata');
        }
    }, [fishGroupId]);

    const targetId = activeFishGroupId ?? 0;
    const {
        data: detail,
        isLoading,
        error,
    } = useGetFishGroupDetail(clientId, targetId, {
        enabled: isOpen && activeFishGroupId != null,
    });
    // Pre-fetch lineage to know whether discard is allowed (and prime the cache
    // for the Lineage tab). React Query dedupes if the user later opens the
    // tab — no extra request.
    const { data: lineage } = useGetFishGroupLineage(clientId, targetId, {
        enabled: isOpen && activeFishGroupId != null,
    });
    const hasTransfers =
        (lineage?.flat.ancestors.length ?? 0) + (lineage?.flat.descendants.length ?? 0) > 0;

    const title = (
        <div className={styles.titleRow}>
            <TitleHeader detail={detail} fallbackName={t('detail.loadingTitle')} />
            {detail && (
                <FishGroupActionsBar
                    clientId={clientId}
                    detail={detail}
                    hasTransfers={hasTransfers}
                />
            )}
        </div>
    );

    const body = (
        <div>
            {isLoading && <Spinner size="lg" />}
            {error && <SomethingHappened />}
            {!isLoading && !error && detail && activeFishGroupId != null && (
                <>
                    <div className={styles.tabs}>
                        <Tabs<TabKey>
                            value={activeTab}
                            onChange={(tab) => setActiveTab(tab)}
                            items={[
                                { value: 'metadata', label: t('detail.tabs.metadata') },
                                { value: 'locations', label: t('detail.tabs.locations') },
                                { value: 'events', label: t('detail.tabs.events') },
                                { value: 'lineage', label: t('detail.tabs.lineage') },
                            ]}
                        />
                    </div>
                    <div className={styles.tabContent}>
                        {activeTab === 'metadata' && (
                            <MetadataTab clientId={clientId} detail={detail} />
                        )}
                        {activeTab === 'locations' && (
                            <LocationsTab clientId={clientId} fishGroupId={activeFishGroupId} />
                        )}
                        {activeTab === 'events' && (
                            <EventsTab clientId={clientId} fishGroupId={activeFishGroupId} />
                        )}
                        {activeTab === 'lineage' && (
                            <LineageTab
                                clientId={clientId}
                                fishGroupId={activeFishGroupId}
                                onNavigateToFishGroup={(id) => {
                                    setActiveFishGroupId(id);
                                    setActiveTab('metadata');
                                }}
                            />
                        )}
                    </div>
                </>
            )}
        </div>
    );

    return (
        <ModalComponent
            isOpen={isOpen}
            onClose={onClose}
            title={title}
            body={body}
            size="fullscreen"
        />
    );
}

interface TitleHeaderProps {
    detail: FishGroupDetail | null;
    fallbackName: string;
}

function TitleHeader(props: TitleHeaderProps) {
    const { detail, fallbackName } = props;

    if (!detail) {
        return <span className={styles.titleName}>{fallbackName}</span>;
    }

    return (
        <div className={styles.title}>
            <span className={styles.titleName}>{detail.name}</span>
            <FishGroupStatusBadge status={detail.status} />
            {detail.currentLocation && (
                <span className={styles.titleMeta}>
                    {detail.currentLocation.cageName} ({detail.currentLocation.locationName})
                </span>
            )}
            {detail.lifecycleStartedAt && (
                <span className={styles.titleMeta}>
                    {formatDateTimeWorded(detail.lifecycleStartedAt, 'minute')}
                    {detail.lifecycleEndedAt
                        ? ` → ${formatDateTimeWorded(detail.lifecycleEndedAt, 'minute')}`
                        : ' →'}
                </span>
            )}
        </div>
    );
}
